import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { documentConfigStorageUtils } from '@/utils/localStorageUtils';

interface DocumentConfig {
  id: number;
  default_document_name: string;
  alias_document_english: string;
  alias_document_french: string;
  parser_function: string;
  status: string;
}

interface LienSummaryConfigState {
  documents: DocumentConfig[];
  loading: boolean;
  error: string | null;
  selectedDocument: DocumentConfig | null;
}

// Try to load initial state from localStorage as fallback
const loadInitialState = (): LienSummaryConfigState => {
  const storageDocuments = documentConfigStorageUtils.loadDocumentConfig();
  
  return {
    documents: storageDocuments || [],
    loading: false,
    error: null,
    selectedDocument: null,
  };
};

const initialState: LienSummaryConfigState = loadInitialState();

const lienSummaryConfigSlice = createSlice({
  name: 'lienSummaryConfig',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    
    // Set documents data
    setDocuments: (state, action: PayloadAction<DocumentConfig[]>) => {
      state.documents = action.payload;
      state.loading = false;
      state.error = null;
      
      // Save to localStorage as backup
      try {
        documentConfigStorageUtils.saveDocumentConfig(action.payload);
      } catch (error) {
        console.warn('Failed to save documents to localStorage:', error);
      }
    },
    
    // Update a single document
    updateDocument: (state, action: PayloadAction<DocumentConfig>) => {
      const index = state.documents.findIndex(doc => doc.id === action.payload.id);
      if (index !== -1) {
        state.documents[index] = action.payload;
        
        // Save updated documents to localStorage as backup
        try {
          documentConfigStorageUtils.saveDocumentConfig(state.documents);
        } catch (error) {
          console.warn('Failed to save updated documents to localStorage:', error);
        }
      }
    },
    
    // Set selected document
    setSelectedDocument: (state, action: PayloadAction<DocumentConfig | null>) => {
      state.selectedDocument = action.payload;
    },
    
    // Set error state
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Load documents from localStorage
    loadFromStorage: (state) => {
      try {
        const storageDocuments = documentConfigStorageUtils.loadDocumentConfig();
        if (storageDocuments && storageDocuments.length > 0) {
          state.documents = storageDocuments;
          state.error = null;
          console.info('Loaded document config from localStorage');
        }
      } catch (error) {
        console.warn('Failed to load documents from localStorage:', error);
      }
    },
    
    // Reset state
    reset: (state) => {
      state.documents = [];
      state.loading = false;
      state.error = null;
      state.selectedDocument = null;
      
      // Clear localStorage as well
      try {
        documentConfigStorageUtils.clearDocumentConfig();
      } catch (error) {
        console.warn('Failed to clear document config localStorage:', error);
      }
    }
  }
});

export const {
  setLoading,
  setDocuments,
  updateDocument,
  setSelectedDocument,
  setError,
  clearError,
  loadFromStorage,
  reset
} = lienSummaryConfigSlice.actions;

export default lienSummaryConfigSlice.reducer;